import type { ReactNode } from 'react';

import * as HoverCardPrimitive from '@radix-ui/react-hover-card';
import type { LucideIcon } from 'lucide-react';
import { NavLink } from 'react-router-dom';

import { cn } from '../../lib/cn';
import { Z } from '../../tokens/z-index';

export interface SidebarFlyoutItem {
  /** Destination route. */
  to: string;
  /** Visible label (already translated by caller). */
  label: string;
  /** Optional icon rendered to the left of the label. */
  icon?: LucideIcon;
  /** Caller pre-computes active state (pathname matching). */
  isActive?: boolean;
}

export interface SidebarFlyoutProps {
  /** Trigger element (usually a NavLink wrapping an icon). */
  children: ReactNode;
  /**
   * Header label rendered inside the popover. Corresponds to the group name
   * when `items` is provided, or the item name when it isn't.
   */
  label: string;
  /**
   * Optional list of sub-items. If omitted or empty, the flyout only shows
   * the label (tooltip-style).
   */
  items?: SidebarFlyoutItem[];
  /** Milliseconds before opening on hover/focus. Default: 120. */
  openDelay?: number;
  /** Milliseconds before closing after mouseleave/blur. Default: 200. */
  closeDelay?: number;
  /** Callback fired when user activates a sub-item (for closing drawers, etc). */
  onItemNavigate?: () => void;
}

/**
 * Side fly-out menu for the collapsed sidebar. Built on @radix-ui/react-hover-card.
 *
 * - Opens on hover (120 ms) and on keyboard focus; closes on mouseleave (200 ms),
 *   blur, or Esc. Radix handles a "safe triangle" between trigger and content
 *   so diagonal mouse trajectories don't accidentally dismiss the popover.
 * - Renders in a portal at z-index 300 (Z.DROPDOWN) so it sits above the
 *   sidebar (200) and any page content, but below modals (400).
 * - Only decorative — the trigger itself must remain a valid navigation
 *   target (NavLink). Click-based users keep full navigation even if they
 *   never hover.
 */
export function SidebarFlyout({
  children,
  label,
  items,
  openDelay = 120,
  closeDelay = 200,
  onItemNavigate,
}: SidebarFlyoutProps) {
  const hasItems = !!items && items.length > 0;

  return (
    <HoverCardPrimitive.Root openDelay={openDelay} closeDelay={closeDelay}>
      <HoverCardPrimitive.Trigger asChild>{children}</HoverCardPrimitive.Trigger>
      <HoverCardPrimitive.Portal>
        <HoverCardPrimitive.Content
          side="right"
          align="start"
          sideOffset={12}
          style={{ zIndex: Z.DROPDOWN }}
          className={cn(
            'rounded-lg border border-slate-200 bg-white shadow-lg min-w-[200px] py-1',
            'animate-in fade-in-0 zoom-in-95',
            'data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95',
          )}
          role={hasItems ? 'navigation' : undefined}
          aria-label={label}
        >
          <div className="px-3 py-2 text-xs font-heading font-semibold uppercase tracking-wider text-slate-500">
            {label}
          </div>
          {hasItems && items ? (
            <ul className="list-none p-0 m-0">
              {items.map((item) => {
                const ItemIcon = item.icon;
                return (
                  <li key={item.to} className="list-none">
                    <NavLink
                      to={item.to}
                      onClick={onItemNavigate}
                      className={cn(
                        'flex items-center gap-2 px-3 py-2 text-sm font-body',
                        item.isActive
                          ? 'bg-primary-500/10 text-primary-700 font-medium'
                          : 'text-slate-700 hover:bg-slate-100',
                      )}
                    >
                      {ItemIcon ? <ItemIcon className="h-4 w-4 shrink-0" aria-hidden /> : null}
                      <span className="truncate">{item.label}</span>
                    </NavLink>
                  </li>
                );
              })}
            </ul>
          ) : null}
          <HoverCardPrimitive.Arrow className="fill-white" width={10} height={6} />
        </HoverCardPrimitive.Content>
      </HoverCardPrimitive.Portal>
    </HoverCardPrimitive.Root>
  );
}
